import { useEffect, useState } from "react";
import { X, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AddTickerDialog({ open, onOpenChange, onAdded }) {
  const [query, setQuery] = useState("");
  const [assetType, setAssetType] = useState("all"); // all | stock | crypto
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.searchTickers(query, assetType === "all" ? undefined : assetType);
        setResults(res);
      } catch (e) {
        toast.error("Search failed");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, assetType]);

  const add = async (item) => {
    try {
      const payload =
        item.type === "crypto"
          ? {
              symbol: `${item.symbol.toUpperCase()}-USD`,
              display_symbol: item.symbol.toUpperCase(),
              name: item.name,
              asset_type: "crypto",
            }
          : {
              symbol: item.symbol,
              display_symbol: item.symbol,
              name: item.name,
              asset_type: "stock",
            };
      await api.addToWatchlist(payload);
      toast.success(`${item.symbol} added`);
      onAdded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="add-ticker-dialog"
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-24 px-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-lg bg-[#0a0a0a] border border-zinc-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-12 border-b border-zinc-800">
          <span className="font-display text-lg font-black tracking-tighter">
            ADD TICKER
          </span>
          <button
            data-testid="add-ticker-close"
            onClick={() => onOpenChange(false)}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex gap-0 border border-zinc-800">
            {["all", "stock", "crypto"].map((t) => (
              <button
                key={t}
                data-testid={`asset-filter-${t}`}
                onClick={() => setAssetType(t)}
                className={`flex-1 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors border-r border-zinc-800 last:border-r-0 ${
                  assetType === t ? "bg-white text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 border border-zinc-800 focus-within:border-white px-3 py-2">
            <MagnifyingGlass size={14} weight="bold" className="text-zinc-500" />
            <input
              data-testid="add-ticker-search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search e.g. AAPL, Tesla, bitcoin, sol..."
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
            />
          </div>

          <div className="max-h-80 overflow-y-auto border border-zinc-900" data-testid="search-results">
            {loading && <div className="p-3 text-xs text-zinc-500">Searching…</div>}
            {!loading && query && results.length === 0 && (
              <div className="p-3 text-xs text-zinc-500">No matches</div>
            )}
            {results.map((r, i) => (
              <div
                key={`${r.type}-${r.symbol}-${r.coin_id || i}`}
                data-testid={`search-result-${r.symbol}`}
                className="flex items-center justify-between px-3 py-2 border-b border-zinc-900 hover:bg-[#141414] transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {r.image && <img src={r.image} alt="" className="w-4 h-4" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold text-sm">{r.symbol}</span>
                      <span className="overline text-[9px] text-zinc-600">{r.type}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.name}</div>
                  </div>
                </div>
                <button
                  data-testid={`add-btn-${r.symbol}`}
                  onClick={() => add(r)}
                  className="border border-zinc-700 hover:border-[#00FF66] hover:text-[#00FF66] text-white h-8 px-3 text-[10px] uppercase font-bold tracking-widest flex items-center gap-1 transition-colors"
                >
                  <Plus size={10} weight="bold" /> ADD
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
